import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';

import { ExpansableCardComponent } from '../expansable-card/expansable-card.component';
import { LoadingComponent } from '../loading/loading.component';
import { ProductV2Service } from '../../../core/services/product-v2.service';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { CartProductItemComponent } from '../cart-product-item/cart-product-item.component';
import { Plan } from '../../../core/models/plan.model';

type ModalStep = 'choice' | 'quantity' | 'plan';

@Component({
  selector: 'umkt-change-plan-modal',
  standalone: true,
  imports: [CommonModule, ExpansableCardComponent, LoadingComponent, I18nPipe, CartProductItemComponent],
  templateUrl: './change-plan-modal.component.html',
  styleUrls: ['./change-plan-modal.component.scss']
})
export class ChangePlanModalComponent implements OnChanges {

  @Input() open = false;
  @Input() dismissible = true;
  @Input() initialQuantity = 1;
  @Input() canChangeQuantity = false;
  @Input() canChangePlan = false;
  @Input() productUuid?: string;
  @Input() plan?:  Plan | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() confirmQuantityChange = new EventEmitter<number>();
  @Output() selectPlan = new EventEmitter<any>();
  @Output() confirmPlanChange = new EventEmitter<any>();

  private productService = inject(ProductV2Service);

  currentStep: ModalStep = 'choice';
  currentPlanIndex: any;
  quantity = 1;

  product: any;
  selectedVariation: any = null;

  mappedProduct: any;
  loadingProduct = false;

  ngOnChanges() {
    if (this.open) {
      this.quantity = this.initialQuantity || 1;
      this.setInitialStep();

      // Quantidade direta (ex.: sessão fixa) não carrega `product` via API; ainda assim precisamos de mappedProduct.
      if (this.currentStep === 'quantity') {
        this.buildMappedProduct();
      } else if (this.product) {
        this.buildMappedProduct();
      }
    }
  }

  private setInitialStep() {
    if (this.canChangeQuantity && this.canChangePlan) {
      this.currentStep = 'choice';
    } else if (this.canChangeQuantity) {
      this.currentStep = 'quantity';
    } else if (this.canChangePlan) {
      this.selectPlanStep();
    }
  }

  selectQuantity() {
    this.currentStep = 'quantity';
    this.buildMappedProduct();
  }

  selectPlanStep() {
    if (!this.productUuid) return;

    this.currentStep = 'plan';
    this.fetchProduct();
  }

  back() {
    if (this.canChangeQuantity && this.canChangePlan) {
      this.currentStep = 'choice';
    } else {
      this.close();
    }
  }

  close() {
    this.closed.emit();
    this.currentStep = 'choice';
  }

  confirmQuantity() {
    this.confirmQuantityChange.emit(this.quantity);
    this.close();
  }

  fetchProduct() {
    this.loadingProduct = true;

    this.productService.getProductByUuid(this.productUuid!).subscribe({
      next: (res) => {
        this.product = res.data;
        this.loadingProduct = false;

        if (this.product?.variations && this.plan?.productUuid) {
          const index = this.product.variations.findIndex(
            (item: any) => item.uuid === this.plan?.productUuid
          );

          if (index !== -1) {
            this.selectedVariation = this.product.variations[index];
            this.currentPlanIndex = index; 
          }
        }
        this.buildMappedProduct();
      },
      error: () => { 
        this.loadingProduct = false; 
      }
    });
  }

  onSelectVariation(index: number) {
    if (this.product?.variations) {
      this.selectedVariation = this.product.variations[index];
      this.buildMappedProduct();
    }
  }

  onAddToCartFromCard(event: { plan?: any | null; quantity: number }) {
    if (event.plan) {
      this.selectedVariation = event.plan;
      this.buildMappedProduct();
    }
  }

  confirmOrder() {
    if (!this.selectedVariation) return;

    this.confirmPlanChange.emit(this.selectedVariation);
    this.close();
  }

  onChangeQty(event: { planId: string; quantity: number }) {
    if (event.quantity < 1) {
      this.quantity = 1;
      return;
    }

    this.quantity = event.quantity;

    if (this.mappedProduct?.plans?.length) {
      this.mappedProduct.plans[0].quantity = this.quantity;
    }
  }

  private buildMappedProduct() {
    const parentUuid = this.productUuid || this.plan?.parentProduct?.uuid || '';
    this.mappedProduct = {
      id: parentUuid,
      uuid: parentUuid,
      name: this.plan?.productName || 'Plano',
      categories: ['business'],
      plans: [
        {
          id: this.plan?.productUuid,
          name: this.plan?.variationName || 'Licenças',
          quantity: this.quantity,
          unitPrice: this.plan?.price || 0,
          paymentRecurrence: this.plan?.billingCycle || 'monthly'
        }
      ]
    };
  }
}